/**
 * <TutorStepHeading> — le titre numéroté d'une partie d'un formulaire en deux temps (molecule).
 *
 * « Ma dispo » se remplit en deux parties qu'on lit dans l'ordre (legacy `MaDispoEditor` →
 * `StepLabel`) : ① la date de démarrage, ② les heures. Le numéro sur sa pastille à la couleur du
 * bouton de l'univers, le titre, et sous le titre la consigne quand il y en a une, alignée sur lui.
 *
 * Ce n'est pas l'en-tête d'une étape de parcours (`<Header steps>`) : les deux parties sont sur la
 * même page, et l'une ne cache pas l'autre.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"

/** La pastille du numéro — un peu plus haute qu'une ligne du titre. */
const NUMBER_SIZE = 24

export type TutorStepHeadingProps = {
    number: number
    title: string
    /** La consigne, sous le titre. */
    hint?: string
    testID?: string
}

export function TutorStepHeading(props: TutorStepHeadingProps) {
    const { number, title, hint, testID } = props

    return (
        <Stack gap="1" testID={testID}>
            <Row gap="3" align="center">
                <View style={styles.number} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
                    <Text size="text-sm" weight="bold" color="text-on-role">
                        {String(number)}
                    </Text>
                </View>
                <Text size="text-lg" weight="bold" style={styles.title} accessibilityRole="header">
                    {title}
                </Text>
            </Row>
            {hint != null && hint !== "" && (
                <Text size="text-md" color="text-muted" style={styles.hint}>
                    {hint}
                </Text>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    number: {
        width: NUMBER_SIZE,
        height: NUMBER_SIZE,
        borderRadius: NUMBER_SIZE / 2,
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        backgroundColor: theme.colors["role-fill"],
    },
    title: { flex: 1, flexShrink: 1 },
    /** Sous le titre, pas sous la pastille. */
    hint: { marginLeft: NUMBER_SIZE + theme.spacing["3"] },
}))
